import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, AuthState, LoginRequest, SignupRequest } from '../types';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';

interface AuthStore extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  signup: (userData: SignupRequest) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  initializeAuth: () => void;
  setUser: (user: User) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => {
      // Listen for auth logout events from API interceptor
      if (typeof window !== 'undefined') {
        window.addEventListener('auth:logout', () => {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        });
      }
      
      return {
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true });
        try {
          const tokenResponse = await apiService.login(credentials);
          const token = tokenResponse.access_token;

          // Store token in API service and localStorage
          apiService.setAuthToken(token);

          // Fetch full user profile from API
          const user = await apiService.getCurrentUser();

          // Store user data in localStorage for persistence
          localStorage.setItem('user', JSON.stringify(user));

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          set({ isLoading: false });
          const message = error.response?.data?.message || 'Login failed';
          toast.error(message);
          throw error;
        }
      },

      signup: async (userData: SignupRequest) => {
        set({ isLoading: true });
        try {
          const user = await apiService.signup(userData);
          
          // After successful signup, automatically log in
          await get().login({
            email: userData.email,
            password: userData.password,
          });
        } catch (error: any) {
          set({ isLoading: false });
          const message = error.response?.data?.message || 'Signup failed';
          toast.error(message);
          throw error;
        }
      },

      logout: () => {
        apiService.removeAuthToken();
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
        toast.success('Logged out successfully');
      },

      clearError: () => {
        // This would clear any error state if we had one
      },

      initializeAuth: async () => {
        const token = apiService.getAuthToken();
        const savedUser = localStorage.getItem('user');
        
        if (token && savedUser) {
          try {
            // Try to fetch fresh user data from API
            try {
              const user = await apiService.getCurrentUser();
              localStorage.setItem('user', JSON.stringify(user));
              set({
                user,
                token,
                isAuthenticated: true,
                isLoading: false,
              });
            } catch (error: any) {
              // If API call fails with 401, user doesn't exist or token is invalid
              if (error.response?.status === 401) {
                // Clear invalid auth data
                apiService.removeAuthToken();
                localStorage.removeItem('user');
                set({
                  user: null,
                  token: null,
                  isAuthenticated: false,
                  isLoading: false,
                });
              } else {
                // For other errors, try using saved user data as fallback
                try {
                  const user = JSON.parse(savedUser);
                  set({
                    user,
                    token,
                    isAuthenticated: true,
                    isLoading: false,
                  });
                } catch {
                  // Invalid saved data, clear it
                  apiService.removeAuthToken();
                  localStorage.removeItem('user');
                  set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                    isLoading: false,
                  });
                }
              }
            }
          } catch {
            // Invalid saved data, clear it
            apiService.removeAuthToken();
            localStorage.removeItem('user');
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } else {
          // No token or user, ensure we're logged out
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setUser: (user: User) => {
        localStorage.setItem('user', JSON.stringify(user));
        set({ user });
      },
      };
    },
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
